import React, { useEffect, useState } from 'react';
import { Calendar as CalendarIcon, CheckCircle2, Loader2, Users, User, ArrowRight } from 'lucide-react';
import api from '../services/api';
import { toast } from 'sonner';

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('academy_token') || ''}` });

const OPTIONS = [
  {
    key: 'option_1',
    title: 'Option 1 · Add Required Friday Classes',
    hook: 'Full 180 in-person days — 5 days a week.',
    schedule: 'Monday–Thursday · 12:30 PM – 3:30 PM  +  Friday mandatory in-person class (pickup 1:30 PM).',
    structure: 'Adding ~33 Friday sessions increases weekly instructional days from 4 to 5.',
    endDate: 'In-Person End Date: June 11, 2027 · Reaches 180 in-person days.',
    accent: 'from-sky-600 to-sky-800',
    border: 'border-sky-500',
  },
  {
    key: 'option_2',
    title: 'Option 2 · Digital Completion After June 11',
    hook: '160 in-person days · 20 finished online.',
    schedule: 'Monday–Thursday · 12:30 PM – 3:30 PM.',
    structure: 'Complete 160 in-person days by June 11, 2027. Remaining 20 required days completed online.',
    endDate: 'In-Person End Date: June 11, 2027 (HandleLife Academy) · Digital Completion: June 14 – July 9, 2027 (SOLO by SAA).',
    accent: 'from-emerald-600 to-emerald-800',
    border: 'border-emerald-500',
  },
  {
    key: 'option_3',
    title: 'Option 3 · Increased Daily Workload & Pace',
    hook: 'Finish 180 in-person days entirely on M–Th.',
    schedule: 'Monday–Thursday · 12:30 PM – 3:30 PM with doubled daily core subject coverage.',
    structure: 'Accelerates daily credit (1.125 day-equivalents/M–Th session). Homework likely to complete the 180-day curriculum in fewer calendar days.',
    endDate: 'In-Person End Date: June 10, 2027 · Reaches 180 instructional day-equivalents fully in-person.',
    accent: 'from-violet-600 to-violet-800',
    border: 'border-violet-500',
  },
];

const SHARED_HOLIDAYS = [
  ['Labor Day', 'September 7, 2026'],
  ['Veterans Day', 'November 11, 2026'],
  ['Thanksgiving Break', 'November 23 – 27, 2026'],
  ['Winter Break', 'December 21, 2026 – January 8, 2027'],
  ['MLK Jr. Day', 'January 18, 2027'],
  ["Presidents' Day", 'February 15, 2027'],
  ['Spring Break / Good Friday', 'March 22 – April 2, 2027  (Good Friday: March 26)'],
  ['Memorial Day', 'May 31, 2027'],
];

const CalendarSelection = () => {
  const [choice, setChoice] = useState('');
  const [track, setTrack] = useState('');
  const [familyName, setFamilyName] = useState('');
  const [familyEmail, setFamilyEmail] = useState('');
  const [studentsCsv, setStudentsCsv] = useState('');
  const [comments, setComments] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);
  const [loading, setLoading] = useState(true);

  // If the family has already submitted, pre-fill so they can review/edit
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get('/academy/calendar-selection/mine', { headers: authHeaders() });
        if (r.data?.submission) {
          const s = r.data.submission;
          setFamilyName(s.family_name || '');
          setFamilyEmail(s.family_email || '');
          setStudentsCsv((s.student_names || []).join(', '));
          setChoice(s.option_choice || '');
          setTrack(s.track_preference || '');
          setComments(s.comments || '');
          setSubmitted(s);
        }
      } catch { /* not authed — that's fine, the form is public */ }
      finally { setLoading(false); }
    })();
  }, []);

  const submit = async (e) => {
    e?.preventDefault();
    if (!choice) return toast.error('Please select a schedule option.');
    if (!track) return toast.error('Please pick a track preference.');
    if (!familyName.trim()) return toast.error('Please enter your family name.');
    if (!familyEmail.trim()) return toast.error('Please enter your email.');
    setSubmitting(true);
    try {
      const payload = {
        family_name: familyName.trim(),
        family_email: familyEmail.trim(),
        student_names: studentsCsv.split(',').map((s) => s.trim()).filter(Boolean),
        option_choice: choice,
        track_preference: track,
        comments: comments.trim(),
      };
      const r = await api.post('/academy/calendar-selection', payload, { headers: authHeaders() });
      setSubmitted({ ...payload, submitted_at: new Date().toISOString() });
      if (r.data?.email_sent) toast.success('Response received. Margaret has been notified.');
      else toast.success('Response received.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not submit. Please try again.');
    } finally { setSubmitting(false); }
  };

  if (loading) {
    return <div className="min-h-screen bg-black text-white flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-emerald-400" /></div>;
  }

  return (
    <div className="min-h-screen bg-[#0a0a12] text-white pt-24 pb-24 px-6" data-testid="calendar-selection-page">
      <div className="container mx-auto max-w-4xl">

        <div className="flex items-center gap-2 mb-3">
          <CalendarIcon className="w-4 h-4 text-emerald-400" />
          <span className="text-[10px] tracking-[0.4em] uppercase font-black text-emerald-400">Action Required · Tier 3 Families</span>
        </div>
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black leading-tight">2026–2027 Academic Calendar &amp; Schedule Selection</h1>
        <p className="text-zinc-300 mt-4 max-w-2xl leading-relaxed">
          To accommodate requests to complete in-person instruction by <strong>June 11, 2027</strong>, while meeting our required
          <strong> 180 instructional days</strong> and observing standard holiday breaks, we've created three schedule options.
        </p>
        <p className="text-zinc-400 mt-2 text-sm max-w-2xl">
          Because we prioritize <strong className="text-white">flexibility with accountability</strong>, individual family
          decisions are possible. You are <strong className="text-white">not required to choose the same option as other families.</strong>
        </p>

        {submitted && (
          <div className="mt-6 border-2 border-emerald-500 bg-emerald-500/10 px-5 py-4 flex items-start gap-3" data-testid="calendar-already-submitted">
            <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
            <div>
              <div className="text-[10px] uppercase tracking-widest font-black text-emerald-400">Response On File</div>
              <div className="text-sm text-white mt-1">
                We received your response on {new Date(submitted.submitted_at).toLocaleDateString()}. You can update your selection below —
                the newest submission overrides the previous one.
              </div>
            </div>
          </div>
        )}

        {/* Shared calendar snapshot */}
        <div className="mt-10 border-2 border-zinc-800 bg-zinc-900/40 p-5">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-[10px] uppercase tracking-[0.35em] font-black text-zinc-400">Instructional Base</span>
          </div>
          <div className="grid sm:grid-cols-3 gap-3 text-sm">
            <div><span className="text-zinc-500 text-xs uppercase tracking-widest block">Days</span><span className="font-black">180</span></div>
            <div><span className="text-zinc-500 text-xs uppercase tracking-widest block">Start</span><span className="font-black">Sept 8, 2026</span></div>
            <div><span className="text-zinc-500 text-xs uppercase tracking-widest block">Class Hours</span><span className="font-black">M–Th · 12:30–3:30 PM</span></div>
          </div>
          <div className="mt-5">
            <div className="text-[10px] uppercase tracking-[0.35em] font-black text-zinc-400 mb-2">Shared Holidays &amp; Breaks (All Options)</div>
            <ul className="text-sm text-zinc-300 space-y-1">
              {SHARED_HOLIDAYS.map(([name, date]) => (
                <li key={name}><span className="text-white font-bold">{name}:</span> <span className="text-zinc-400">{date}</span></li>
              ))}
            </ul>
          </div>
        </div>

        {/* Options */}
        <div className="mt-10 space-y-4" data-testid="calendar-options">
          <h2 className="text-lg font-black uppercase tracking-widest text-zinc-300">Compare the Three Options</h2>
          {OPTIONS.map((opt) => {
            const active = choice === opt.key;
            return (
              <button
                type="button"
                key={opt.key}
                onClick={() => setChoice(opt.key)}
                className={`w-full text-left border-2 transition-all p-6 relative ${active ? `${opt.border} bg-white/[0.04] shadow-xl` : 'border-zinc-800 bg-zinc-900/30 hover:border-zinc-600'}`}
                data-testid={`calendar-option-${opt.key}`}
              >
                <div className={`inline-block bg-gradient-to-r ${opt.accent} text-white text-[10px] uppercase tracking-[0.3em] font-black px-2 py-0.5`}>{opt.hook}</div>
                <div className="text-xl sm:text-2xl font-black text-white mt-3 leading-snug">{opt.title}</div>
                <div className="mt-4 space-y-2 text-sm text-zinc-300">
                  <div><span className="text-zinc-500 text-xs uppercase tracking-widest block mb-0.5">Schedule</span>{opt.schedule}</div>
                  <div><span className="text-zinc-500 text-xs uppercase tracking-widest block mb-0.5">Structure</span>{opt.structure}</div>
                  <div><span className="text-zinc-500 text-xs uppercase tracking-widest block mb-0.5">End Dates</span>{opt.endDate}</div>
                </div>
                {active && (
                  <div className="absolute top-4 right-4 bg-white text-black w-6 h-6 flex items-center justify-center rounded-full font-black" data-testid={`calendar-option-${opt.key}-checked`}>
                    <CheckCircle2 className="w-5 h-5" />
                  </div>
                )}
              </button>
            );
          })}
        </div>

        {/* Form */}
        <form onSubmit={submit} className="mt-12 border-2 border-white bg-black/60 p-6 space-y-5" data-testid="calendar-form">
          <div>
            <label className="text-[10px] uppercase tracking-[0.35em] font-black text-zinc-400 block mb-2">Track Preference</label>
            <div className="grid sm:grid-cols-2 gap-3">
              <button type="button" onClick={() => setTrack('group')}
                      className={`flex items-center gap-3 border-2 p-4 ${track === 'group' ? 'border-emerald-500 bg-emerald-500/10' : 'border-zinc-700 hover:border-zinc-500'}`}
                      data-testid="calendar-track-group">
                <Users className="w-5 h-5 text-emerald-400" />
                <div className="text-left">
                  <div className="font-black">Group Track</div>
                  <div className="text-xs text-zinc-400">We prefer to move with the cohort</div>
                </div>
              </button>
              <button type="button" onClick={() => setTrack('individual')}
                      className={`flex items-center gap-3 border-2 p-4 ${track === 'individual' ? 'border-emerald-500 bg-emerald-500/10' : 'border-zinc-700 hover:border-zinc-500'}`}
                      data-testid="calendar-track-individual">
                <User className="w-5 h-5 text-emerald-400" />
                <div className="text-left">
                  <div className="font-black">Individual Track</div>
                  <div className="text-xs text-zinc-400">Our family chooses independently</div>
                </div>
              </button>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-[10px] uppercase tracking-[0.35em] font-black text-zinc-400 block mb-2">Family (Last Name)</label>
              <input value={familyName} onChange={(e) => setFamilyName(e.target.value)}
                     className="w-full bg-zinc-950 border-2 border-zinc-700 focus:border-white px-3 py-2 text-white outline-none"
                     data-testid="calendar-family-name" placeholder="Williamson" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.35em] font-black text-zinc-400 block mb-2">Parent Email</label>
              <input value={familyEmail} onChange={(e) => setFamilyEmail(e.target.value)}
                     type="email"
                     className="w-full bg-zinc-950 border-2 border-zinc-700 focus:border-white px-3 py-2 text-white outline-none"
                     data-testid="calendar-family-email" placeholder="you@family.com" />
            </div>
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-[0.35em] font-black text-zinc-400 block mb-2">Student Name(s) — comma separated</label>
            <input value={studentsCsv} onChange={(e) => setStudentsCsv(e.target.value)}
                   className="w-full bg-zinc-950 border-2 border-zinc-700 focus:border-white px-3 py-2 text-white outline-none"
                   data-testid="calendar-students" placeholder="Brave, Brandon II" />
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-[0.35em] font-black text-zinc-400 block mb-2">Comments (optional)</label>
            <textarea value={comments} onChange={(e) => setComments(e.target.value)} rows={5}
                      className="w-full bg-zinc-950 border-2 border-zinc-700 focus:border-white px-3 py-2 text-white outline-none resize-none"
                      data-testid="calendar-comments"
                      placeholder="Any context you'd like Margaret to know — travel plans, sibling schedules, sport commitments, etc." />
          </div>

          <button type="submit" disabled={submitting}
                  className="w-full sm:w-auto bg-white text-black hover:bg-emerald-400 hover:text-black font-black uppercase tracking-widest text-[13px] px-8 py-4 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-wait"
                  data-testid="calendar-submit-btn">
            {submitting ? (<><Loader2 className="w-4 h-4 animate-spin" />Submitting</>) : (<>Lock In Our Selection <ArrowRight className="w-4 h-4" /></>)}
          </button>
          <p className="text-[11px] text-zinc-500 leading-relaxed">
            Your submission is emailed immediately to Margaret Williamson (Director of Admissions and Program Standards) at Margaret@prsvr.com.
          </p>
        </form>
      </div>
    </div>
  );
};

export default CalendarSelection;
